import { QueryKey, useInfiniteQuery, UseInfiniteQueryOptions } from 'react-query'

import { getCurrentStackId } from 'app/GlobalStaticState'
import {
    queryClient,
    useCanRunStackScopedQueries,
    useDeleteItem,
    useQueryKeyBuilder,
} from 'data/hooks/_helpers'
import { STACK_QUERY_CONFIG } from 'data/reactQueryCache'
import { fetchAndReturn } from 'data/utils/utils'

import { AgentConversation } from './types'

// Conversations
const CONVERSATIONS_LIST_NAME = 'useAgentConversations'
const get_conversations_endpoint = (agent_sid: string, continuation_token?: string) =>
    `agents/${agent_sid}/conversations/${continuation_token ? `?continuation_token=${encodeURIComponent(continuation_token)}` : ''}`

function useConversationsQueryKey() {
    return useQueryKeyBuilder(CONVERSATIONS_LIST_NAME, {
        includeAuthKeys: true,
        includeStackId: true,
    })
}

type ConversationsResponse = {
    results: AgentConversation[]
    continuation_token: string | null
    has_more: boolean
}

type ConversationsOptionsType = Omit<
    UseInfiniteQueryOptions<
        ConversationsResponse,
        unknown,
        ConversationsResponse,
        ConversationsResponse,
        QueryKey
    >,
    'getPreviousPageParam' | 'getNextPageParam'
>

export function useAgentConversations(agent_sid: string, options: ConversationsOptionsType = {}) {
    const enabled = useCanRunStackScopedQueries()
    const query_config: UseInfiniteQueryOptions<ConversationsResponse> = {
        ...(STACK_QUERY_CONFIG as ConversationsOptionsType),
        keepPreviousData: true,
        ...options,
        enabled,
        getNextPageParam: (lastPage) =>
            lastPage.has_more ? lastPage.continuation_token : undefined,
        getPreviousPageParam: () => undefined, // We don't support backwards pagination
    }

    return useInfiniteQuery<ConversationsResponse>(
        useConversationsQueryKey(),
        async ({ pageParam }) => {
            const url = get_conversations_endpoint(agent_sid, pageParam)
            return fetchAndReturn(url) as Promise<ConversationsResponse>
        },
        query_config
    )
}

export function useDeleteAgentConversation(agent_sid: string) {
    return useDeleteItem(useConversationsQueryKey(), get_conversations_endpoint(agent_sid), {
        onSuccess: () => {
            invalidateConversations()
        },
    })
}

export function invalidateConversations() {
    return queryClient.invalidateQueries([CONVERSATIONS_LIST_NAME, getCurrentStackId()])
}
